import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { HOME_DEFAULTS } from "../lib/homeDefaults";

const CATEGORIES = [
  { key: "loafers", label: "Loafers", blurb: "Slip-and-go elegance" },
  { key: "slip-ons", label: "Slip-Ons", blurb: "Unfussy, unhurried" },
  { key: "chelsea", label: "Chelsea", blurb: "Elastic-gored elegance" },
  { key: "derby", label: "Derby", blurb: "Open-laced refinement" },
  { key: "monk", label: "Monk", blurb: "Buckled sophistication" },
  { key: "heritage", label: "Heritage Comfort", blurb: "Agra artistry, daily comfort" },
  { key: "wedding", label: "Wedding", blurb: "For the once-in-a-lifetime day" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [c, setC] = useState({});

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 4)));
    api.get("/site-content").then(({ data }) => setC(data || {}));
  }, []);

  const val = (k, fallback) => c[k] || HOME_DEFAULTS[k] || fallback;
  const marqueeItems = (c.marquee || HOME_DEFAULTS.marquee)
    .split("|").map((s) => s.trim()).filter(Boolean);

  return (
    <div>
      {/* HERO */}
      <section className="pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="label-eyebrow mb-6">{val("hero_tag", "FW / 26 Collection")}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              {val("hero_headline", "Handcrafted, one pair at a time.")}
            </h1>
            <p className="mt-8 text-base text-[#666] max-w-md leading-relaxed">
              {val("hero_subtext", "We make shoes the way they used to be made — slowly, in small batches, by people who still care about the difference between good and great.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="bg-[#111] text-white px-8 py-4 text-sm font-medium hover:bg-[#333] transition-colors inline-flex items-center gap-2"
                data-testid="hero-shop-btn"
              >
                Shop the Collection <ArrowRight size={16} />
              </Link>
              <Link
                to="#story"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("story")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border border-[#e5e5e5] text-[#111] px-8 py-4 text-sm font-medium hover:border-[#111] transition-colors"
              >
                Our Story
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative aspect-[4/5] bg-[#f5f2ec] overflow-hidden border border-[#e5e5e5]"
          >
            {val("hero_image_url", "") ? (
              <>
                <img
                  src={val("hero_image_url", "")}
                  alt="K-Walk hero"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                {val("hero_caption", "") && (
                  <div className="absolute bottom-8 left-8 bg-white/90 backdrop-blur px-5 py-3 text-xs uppercase tracking-widest">
                    {val("hero_caption", "")}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-brand text-6xl text-[#111]/30 tracking-widest">K-Walk</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#e5e5e5] py-6 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 2 }).map((_, k) => (
            <div
              key={k}
              className="flex gap-16 pr-16 font-display text-2xl md:text-3xl font-bold tracking-tight"
            >
              {marqueeItems.map((item, i) => (
                <span key={i} className="flex items-center gap-16">
                  <span>{item}</span>
                  <span className="text-[#8c7a6b]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section id="story" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
          <div className="aspect-[4/5] bg-[#f5f2ec] overflow-hidden border border-[#e5e5e5] order-2 md:order-1">
            {val("story_image_url", "") ? (
              <img
                src={val("story_image_url", "")}
                alt="K-Walk atelier"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="label-eyebrow text-[#111]/40">The Atelier</span>
              </div>
            )}
          </div>
          <div className="order-1 md:order-2">
            <div className="label-eyebrow mb-6">Our Story</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              {val("story_heading", "A small workshop, an old idea, a slow craft.")}
            </h2>
            <div className="mt-10 space-y-5 text-[#111] leading-relaxed max-w-lg">
              <p>{val("story_p1", "K-Walk was born from a stubborn belief.")}</p>
              <p className="text-[#666]">{val("story_p2", "")}</p>
              <p className="text-[#666]">{val("story_p3", "")}</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-8 border-t border-[#e5e5e5] pt-8 max-w-md">
              <div>
                <div className="font-display text-3xl font-extrabold">{val("founded_year", "2011")}</div>
                <div className="label-eyebrow mt-1">{val("stat_1_label", "Since")}</div>
              </div>
              <div>
                <div className="font-display text-3xl font-extrabold">{val("artisan_count", "3")}</div>
                <div className="label-eyebrow mt-1">{val("stat_2_label", "Generations")}</div>
              </div>
              <div>
                <div className="font-display text-3xl font-extrabold">{val("steps_count", "Agra")}</div>
                <div className="label-eyebrow mt-1">{val("stat_3_label", "Home")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS BY CATEGORY */}
      <section className="py-16 md:py-24 border-t border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow mb-4">The Collection</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                Seven silhouettes, one language.
              </h2>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-60"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {CATEGORIES.map((cat) => {
              const img = c[`cat_${cat.key}_image`];
              return (
                <Link
                  key={cat.key}
                  to={`/shop?category=${cat.key}`}
                  className="group flex flex-col gap-4"
                  data-testid={`category-tile-${cat.key}`}
                >
                  <div className="relative aspect-[4/5] bg-[#f5f2ec] overflow-hidden border border-[#e5e5e5]">
                    {img ? (
                      <img
                        src={img}
                        alt={cat.label}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-brand text-3xl text-[#111]/40 tracking-widest">
                          {cat.label.slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-display text-xl font-bold">{cat.label}</div>
                    <div className="text-sm text-[#666]">{cat.blurb}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div>
              <div className="label-eyebrow mb-4">Signature Picks</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                Objects of quiet distinction.
              </h2>
            </div>
            <Link
              to="/shop"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium hover:opacity-60"
              data-testid="view-all-link"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* CRAFT BLOCK */}
      <section className="bg-[#111] text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <div className="label-eyebrow text-white/60 mb-6">The Craft</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Hand-lasted. Hand-stitched. Made to be worn.
            </h2>
          </div>
          <div className="flex flex-col justify-end">
            <p className="text-white/70 leading-relaxed max-w-lg">
              Each pair is cut, stitched and finished by our artisans in Agra using premium
              synthetics and traditional construction. Not perfect &mdash; honest. That&apos;s the whole
              point.
            </p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-white/40 pb-1 w-fit hover:border-white transition-colors"
            >
              Start with a pair <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
